import { supabase } from "@/integrations/supabase/client";
import { CreateReagentResult, UploadParams } from "@/types/reagent.types";
import { toast } from "sonner";
import { REAGENT_IMAGES_BUCKET_NAME } from "@/services/storageService";
import { compressImage, getOptimalCompressionSettings } from "@/utils/imageCompression";

/**
 * Uploads a new reagent
 */
export const uploadReagent = async (params: UploadParams): Promise<CreateReagentResult> => {
  try {
    console.log("Uploading new reagent:", params.name);
    
    // Get current user for association
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error("Error getting current user:", userError);
      return { 
        success: false, 
        error: "You must be logged in to upload reagents" 
      };
    }
    
    const userId = userData.user!.id;
    
    // Handle image upload if present
    let imageUrl = null;
    if (params.image) {
      console.log("Image upload - File details:", {
        name: params.image.name,
        size: params.image.size,
        type: params.image.type,
        lastModified: params.image.lastModified
      });

      // Validate file size with progressive compression fallback
      const maxSize = 10 * 1024 * 1024; // 10MB
      let imageToUpload = params.image;
      
      if (params.image.size > maxSize) {
        console.log("Image too large, attempting compression...");
        
        try {
          const compressionSettings = getOptimalCompressionSettings(params.image);
          const compressionResult = await compressImage(params.image, {
            ...compressionSettings,
            maxSizeMB: 8 // More aggressive for large files
          });
          
          imageToUpload = compressionResult.file;
          console.log("Compression successful:", {
            originalSize: compressionResult.originalSize,
            compressedSize: compressionResult.compressedSize,
            reduction: compressionResult.compressionRatio
          });
          
        } catch (compressionError) {
          console.error("Compression failed:", compressionError);
          toast.error("Image file too large. Maximum size is 10MB.");
          return { success: false, error: "Image file too large and compression failed" };
        }
      }
      
      // Generate a safe filename for mobile compatibility
      const timestamp = Date.now();
      const originalName = imageToUpload.name || 'image';
      const fileExtension = originalName.split('.').pop()?.toLowerCase() || 'jpg';
      
      // Android compatibility: Sanitize filename
      const safeFileName = `${timestamp}_reagent_image.${fileExtension}`;
      const uploadPath = `${userId}/${safeFileName}`;
      
      console.log("Image upload - Upload path:", uploadPath);

      // Android compatibility: Determine content type more reliably for camera files
      const getContentType = (fileName: string, providedType: string): string => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        const mimeTypes: { [key: string]: string } = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp'
        };
        
        // Camera files often have missing or incorrect MIME types
        const isLikelyFromCamera = fileName.toLowerCase() === 'image.jpg' || 
                                 fileName.toLowerCase() === 'image.jpeg' || 
                                 fileName.toLowerCase() === 'image.png' ||
                                 !providedType;
        
        // For camera files, prioritize extension-based detection
        if (isLikelyFromCamera && ext && mimeTypes[ext]) {
          console.log("Camera file detected - using extension-based MIME type:", mimeTypes[ext]);
          return mimeTypes[ext];
        }
        
        // Use provided type if valid, otherwise determine from extension
        if (providedType && providedType.startsWith('image/')) {
          return providedType;
        }
        
        return mimeTypes[ext || ''] || 'image/jpeg'; // Default to JPEG for unknown camera files
      };

      const contentType = getContentType(originalName, imageToUpload.type || '');
      console.log("Image upload - Content type determined:", {
        contentType,
        originalType: imageToUpload.type,
        fileName: originalName,
        isLikelyCamera: originalName.toLowerCase() === 'image.jpg' || !imageToUpload.type
      });

      // Upload with retry mechanism
      let uploadAttempts = 0;
      const maxRetries = 3;
      
      while (uploadAttempts < maxRetries) {
        try {
          uploadAttempts++;
          
          const { data: imageData, error: imageError } = await supabase.storage
            .from(REAGENT_IMAGES_BUCKET_NAME)
            .upload(uploadPath, imageToUpload, {
              cacheControl: '3600',
              upsert: false,
              contentType: contentType
            });
            
          if (imageError) {
            throw imageError;
          } else if (imageData) {
            console.log("Image upload successful:", imageData);
            
            // Get public URL for the uploaded image
            const { data: publicUrlData } = supabase.storage
              .from(REAGENT_IMAGES_BUCKET_NAME)
              .getPublicUrl(imageData.path);
              
            imageUrl = publicUrlData.publicUrl;
            console.log("Image public URL generated:", imageUrl);
            break; // Success, exit retry loop
          }
        } catch (uploadError: any) {
          console.error(`Upload attempt ${uploadAttempts} failed:`, uploadError);
          
          if (uploadAttempts >= maxRetries) {
            toast.error(`Failed to upload image after ${maxRetries} attempts: ${uploadError.message}`);
            // Continue without image rather than failing completely
            break;
          } else {
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * uploadAttempts));
          }
        }
      }
    } else {
      console.log("No image provided for upload");
    }
    
    // Parse amount to a number for the database
    const amountValue = parseFloat(params.amount) || 0;
    
    // Create new reagent record
    const reagentData = {
      name: params.name,
      catalogue_number: params.catalogNumber,
      category_id: params.category,
      description: params.description,
      amount: amountValue,
      amount_unit: params.unit,
      storage_conditions: params.storageConditions,
      number_of_packages: parseInt(params.numberOfPackages) || 1,
      // Convert Date to string for database
      expiry_date: params.expiryDate,
      image_url: imageUrl,
      user_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      available: true
    };
    
    // Add optional city/country if provided
    if (params.city) {
      reagentData['city'] = params.city;
    }
    if (params.country) {
      reagentData['country'] = params.country;
    }
    
    // Insert the reagent
    const { data, error } = await supabase
      .from("reagents")
      .insert(reagentData)
      .select();
      
    if (error) {
      console.error("Error creating reagent:", error);
      return {
        success: false,
        error: `Failed to create reagent: ${error.message}`
      };
    }
    
    if (!data || data.length === 0) {
      return {
        success: false,
        error: "Reagent was created but no data was returned"
      };
    }
    
    console.log("Reagent created successfully:", data[0].id);
    
    // Notifications are now handled by database trigger
    console.log('Reagent uploaded successfully, notifications will be sent by database trigger');
    
    return {
      success: true,
      id: data[0].id,
    };
    
  } catch (error: any) {
    console.error("Exception in uploadReagent:", error);
    
    return {
      success: false,
      error: `An unexpected error occurred: ${error.message || 'Unknown error'}`
    };
  }
};